import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  FormField,
  Input,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { LeaderboardAdjustment } from '@/src/types/JamTeamChallengeAnswer';
import { useApi } from '@/src/store/api.context';
import { useJamFacilitator } from '@/src/store/jam-facilitator.context';
import { preProdLogger } from '@/src/utils/log.utils';
import { formatDuration, parseDuration } from '@/src/utils/duration.utils';
import { ConfirmModal } from '@/src/components/common/ConfirmModal';

export interface LeaderboardAdjustmentChallenge {
  challengeId: string;
  challengeTitle: string;
}

interface LeaderboardAdjustmentsProps {
  eventName: string;
  teamName: string;
  adjustment?: LeaderboardAdjustment;
  challenges: LeaderboardAdjustmentChallenge[];
}

// Matches an optional leading minus sign followed by one or more digits (whole numbers only).
const INTEGER_REGEX = /^-?\d+$/;

const isValidInteger = (value: string): boolean => {
  const trimmed = value.trim();
  // An empty field is treated as 0, so it is considered valid.
  return trimmed === '' || INTEGER_REGEX.test(trimmed);
};

const toInteger = (value: string): number => {
  const trimmed = value.trim();
  return trimmed === '' ? 0 : parseInt(trimmed, 10);
};

const LeaderboardAdjustments: React.FC<LeaderboardAdjustmentsProps> = ({
  eventName,
  teamName,
  adjustment,
  challenges,
}) => {
  const { t } = useTranslation();
  const { jamFacilitatorApi } = useApi();
  const { loadTeamProgressData } = useJamFacilitator();
  const { leaderboardAdjustments } = i18nKeys.facilitator.participants;

  const currentPoints = adjustment?.points ?? 0;
  const currentTotalTimeSeconds = adjustment?.totalTimeSeconds ?? 0;

  const [editing, setEditing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pending, setPending] = useState(false);
  const [points, setPoints] = useState(String(currentPoints));
  const [duration, setDuration] = useState(formatDuration(currentTotalTimeSeconds));
  const [selectedChallenge, setSelectedChallenge] = useState<OptionDefinition>({
    label: t(leaderboardAdjustments.noChallenge),
    value: '',
  });

  const challengeOptions = useMemo<OptionDefinition[]>(
    () => [
      { label: t(leaderboardAdjustments.noChallenge), value: '' },
      ...challenges.map((challenge) => ({ label: challenge.challengeTitle, value: challenge.challengeId })),
    ],
    [challenges, t, leaderboardAdjustments.noChallenge]
  );

  const pointsError = !isValidInteger(points) ? t(leaderboardAdjustments.pointsError) : '';
  const timeError = parseDuration(duration) === null ? t(leaderboardAdjustments.timeError) : '';
  const hasError = !!pointsError || !!timeError;

  const startEditing = useCallback(() => {
    // Pre-fill from the current stored adjustment so we don't clobber existing values.
    setPoints(String(currentPoints));
    setDuration(formatDuration(currentTotalTimeSeconds));
    setSelectedChallenge({ label: t(leaderboardAdjustments.noChallenge), value: '' });
    setEditing(true);
  }, [currentPoints, currentTotalTimeSeconds, t, leaderboardAdjustments.noChallenge]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setShowConfirm(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (hasError) {
      return;
    }
    try {
      setPending(true);
      const challengeId = (selectedChallenge.value as string) || undefined;
      // Convert the "m:ss" duration input back into a single signed seconds value for the API.
      const totalTimeSeconds = parseDuration(duration) ?? 0;
      await jamFacilitatorApi.adjustTeamScore(eventName, {
        teamName,
        points: toInteger(points),
        totalTimeSeconds,
        challengeId,
      });
      // Refetch progress so the leaderboard entry and stored adjustment reflect the new standing.
      await loadTeamProgressData(eventName, teamName);
      setEditing(false);
    } catch (err) {
      preProdLogger(err);
    } finally {
      setPending(false);
      setShowConfirm(false);
    }
  }, [hasError, selectedChallenge, jamFacilitatorApi, eventName, teamName, points, duration, loadTeamProgressData]);

  return (
    <Container className="dark-bg" data-testid="leaderboard-adjustments">
      <SpaceBetween direction="vertical" size="s">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box variant="h3">{t(leaderboardAdjustments.title)}</Box>
          {!editing && (
            <ButtonDropdown
              items={[{ text: t(leaderboardAdjustments.edit) ?? '', id: 'edit', iconName: 'edit' }]}
              variant="icon"
              onItemClick={startEditing}
              data-testid="leaderboard-adjustments-actions-btn"
            />
          )}
        </div>
        <Box variant="p" color="text-body-secondary">
          {t(leaderboardAdjustments.description)}
        </Box>
        {editing ? (
          <SpaceBetween direction="vertical" size="m">
            <FormField label={t(leaderboardAdjustments.points)} errorText={pointsError}>
              <Input
                type="number"
                value={points}
                onChange={({ detail }) => setPoints(detail.value)}
                data-testid="leaderboard-adjustments-points-input"
              />
            </FormField>
            <FormField
              label={t(leaderboardAdjustments.totalTime)}
              constraintText={t(leaderboardAdjustments.timeConstraint)}
              errorText={timeError}
            >
              <Input
                value={duration}
                onChange={({ detail }) => setDuration(detail.value)}
                placeholder={t(leaderboardAdjustments.timePlaceholder)}
                data-testid="leaderboard-adjustments-time-input"
              />
            </FormField>
            <FormField label={t(leaderboardAdjustments.challenge)}>
              <Select
                selectedOption={selectedChallenge}
                onChange={({ detail }) => setSelectedChallenge(detail.selectedOption)}
                options={challengeOptions}
                placeholder={t(leaderboardAdjustments.selectChallenge)}
                filteringType="auto"
                data-testid="leaderboard-adjustments-challenge-select"
              />
            </FormField>
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={cancelEditing} disabled={pending}>
                  {t(leaderboardAdjustments.cancel)}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowConfirm(true)}
                  loading={pending}
                  disabled={hasError}
                  data-testid="leaderboard-adjustments-save-btn"
                >
                  {t(leaderboardAdjustments.save)}
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        ) : (
          <ColumnLayout borders="horizontal" columns={2}>
            <Box className="space-between">
              <Box>{t(leaderboardAdjustments.points)}</Box>
              <Box data-testid="leaderboard-adjustments-points">{currentPoints}</Box>
            </Box>
            <Box className="space-between">
              <Box>{t(leaderboardAdjustments.totalTime)}</Box>
              <Box data-testid="leaderboard-adjustments-time">{formatDuration(currentTotalTimeSeconds)}</Box>
            </Box>
          </ColumnLayout>
        )}
      </SpaceBetween>
      <ConfirmModal
        visible={showConfirm}
        title={t(leaderboardAdjustments.confirmTitle)}
        message={t(leaderboardAdjustments.confirmMessage)}
        confirmBtnLabel={t(leaderboardAdjustments.confirmButton)}
        loading={pending}
        onConfirm={() => void handleSave()}
        onCancel={() => setShowConfirm(false)}
        data-testid="leaderboard-adjustments-confirm-modal"
      />
    </Container>
  );
};

export default LeaderboardAdjustments;
